import { Component, computed, effect, inject, input, OnInit, output, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import {
    AddressDetailObject,
    FileObject,
    FolderGridItemObject,
    PersonContactGridItemObject,
    PersonGridItemObject,
} from '@it2go/types';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectFolderGroupsDialogComponent } from './select-folder-groups/select-folder-groups-dialog.component';
import {
    SelectFolderWorkgroupDialogComponent,
} from './select-folder-workgroup-dialog/select-folder-workgroup-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { fb, isAllowed } from '../../../../shared/shared.global-service';
import {
    SelectFolderAddressDialogComponent,
} from './select-folder-address-dialog/select-folder-address-dialog.component';
import { PersonBasicInfoStore } from './person-basic-info.store';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { FilterBuilder } from '../../../helper/filter.builder';
import { carePaymentFrag, personContactGridItemFrag, personDependenceFrag } from '../../../service/types/person.types';
import { ServiceGroupEnum } from '@libs/types/src/services/enum/service-group.enum';
import { PersonSocialDataPutInput } from '@libs/types/src/services/input-type/person/person-social-data.put-input';
import {
    CarePaymentDialogComponent,
} from '../../../../../components/social-service/folder/care-payment/care-payment-dialog.component';
import {
    KeyWorkerComponent,
} from 'packages/client/src/app/components/social-service/person/key-worker/key-worker.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import {
    PersonDependenceDialogComponent,
} from '../../../../../components/social-service/folder/person-dependence-dialog/person-dependence-dialog.component';
import { shortDate, ShortDatePipe } from '../../../../shared/pipes/short-date.pipe';
import { PersonDependenceStateEnum } from '@libs/types/src/services/enum/person-dependence.enum';
import { PersonContactTypeEnum } from '../../../../../../../../types/src/services/enum/person-contact-type.enum';
import { InputFileBtnComponent } from '../../common/input-file-btn/input-file-btn.component';
import { LinkButtonComponent } from '../../../../../components/kit/button/link-button.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { Popover } from '@openng/optimus-ui/popover';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import { NgTemplateOutlet } from '@angular/common';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { IShownProperties, shownPropertiesDefaultValues } from './person-basic-info.types';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { debounceTime } from 'rxjs/operators';
import config from '../../../../../config/config';
import { contentGlobalSuccess } from '../../../content.error';
import { SelectWorkplaceDialogComponent } from './select-workplace-dialog/select-workplace-dialog.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import {
    CommunicationPasswordObject,
} from '@libs/types/src/services/object-type/person/person-communication-password.object';
import { AvatarComponent } from '../../../../shared/components/kit/avatar/avatar.component';
import { PersonInitialsPipe } from '../../../../shared/pipes/personInitials';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import { MapLinkComponent } from '../../../../shared/components/map-link/map-link.component';
import { MapLinkFormatEnum } from '../../../../shared/components/map-link/map-link.types';
import { RuianService } from '../../../../shared/components/map/ruian.service';
import { FolderPersonObject } from '@libs/types/src/services/object-type/folder/folder-person.object';
import {
    FolderPersonActionsComponent,
} from '../../../../../components/social-service/folder/folder-persons/folder-person-actions/folder-person-actions.component';
import {
    FolderPersonsHistoryDialogComponent,
} from '../../../../../components/social-service/folder/folder-persons/folder-persons-history-dialog/folder-persons-history-dialog.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { isFolderPersonActive } from '../../../../../utils/folder.utils';
import { FormatNumberPipe } from '../../../../shared/pipes/format-number.pipe';

@Component({
    selector: 'app-person-basic-info',
    templateUrl: './person-basic-info.component.html',
    styleUrls: ['./person-basic-info.component.scss'],
    providers: [PersonBasicInfoStore, RuianService],
    // zemřelá osoba ze složky nemizí, jen se ztlumí celá karta
    host: { '[class.deceased]': 'isDeceased()' },
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputFileBtnComponent,
        LinkButtonComponent,
        ButtonComponent,
        Popover,
        CheckInputComponent,
        Tabs,
        TabList,
        Tab,
        TranslatePipe,
        TabPanels,
        TabPanel,
        NgTemplateOutlet,
        AddressInputComponent,
        AvatarComponent,
        PersonInitialsPipe,
        FormatPersonPipe,
        ShortDatePipe,
        MapLinkComponent,
        FolderPersonActionsComponent,
        Tooltip,
        FormatNumberPipe,
    ],
})
export class PersonBasicInfoComponent extends SubscriptionComponent implements OnInit {
    protected readonly MapLinkFormatEnum = MapLinkFormatEnum;

    public readonly store = inject(PersonBasicInfoStore);
    public readonly router = inject(Router);
    public readonly route = inject(ActivatedRoute);
    public readonly dialogService = inject(BaseDialogService);
    public readonly translate = inject(TranslateService);
    protected readonly folderStore = inject(FolderGlobalStore);
    private readonly ruianService = inject(RuianService);

    shownProperties = input<IShownProperties>(shownPropertiesDefaultValues);
    person = input.required<PersonGridItemObject>();
    folder = input.required<FolderGridItemObject>();
    /** Pobyt osoby ve složce - vyplněno jen u složek s více osobami. */
    folderPerson = input<FolderPersonObject | null>(null);
    /** Karta se dá na nástěnce chytit za horní panel a přesunout (jen karty dalších osob). */
    movable = input<boolean>(false);

    showPropertiesChanged = output<object>();
    /** Rodič si po změně pobytu (hvězdička, datumy, ukončení) musí znovu načíst osoby složky. */
    folderPersonChanged = output<void>();

    /** Překlopená hvězdička, než dorazí čerstvý `folderPerson` z rodiče. */
    private mainOverride = signal<boolean | null>(null);

    /**
     * Primární osoba složky - může jich být víc. Bez pobytu (jednoosobová složka) je zobrazená
     * osoba vždy primární.
     */
    protected isMainPerson = computed(() => this.mainOverride() ?? this.folderPerson()?.isMain ?? true);

    /** Primární osobu lze přepnout jen u osoby ve složce a v neukončené složce. */
    protected canToggleMain = computed(() =>
        !!this.folderPerson() && !this.folderStore.isTerminated() && this.canEditFolder(),
    );

    protected mainPersonTooltip = computed(() => {
        if (!this.canToggleMain()) {
            return this.isMainPerson() ? 'folder.persons.mainPerson' : 'folder.persons.otherPerson';
        }

        return this.isMainPerson() ? 'folder.persons.actions.unsetMain' : 'folder.persons.actions.setMain';
    });

    /**
     * Vlastník složky (folders.person_id) - na rozdíl od `isMainPerson()` právě jeden. Visí na
     * něm smlouvy, vyúčtování a MPSV, takže údaje celé složky se editují jen z jeho karty.
     */
    protected isFolderOwner = computed(() => {
        // `personId` nemusí být v načteném fragmentu složky, osoba vlastníka v něm je vždy
        const ownerId = this.folder()?.personId ?? this.folder()?.person?.id;

        return !ownerId || this.person().id === ownerId;
    });

    /** Osoba je ve složce, ale její pobyt na službě zrovna neběží (typicky teprve začne). */
    protected isOnService = computed(() => isFolderPersonActive(this.folderPerson()));

    /** Datum úmrtí - grid ho vrací u pobytu, jednoosobová složka ho má rovnou na osobě. */
    protected dateDeath = computed(() => this.folderPerson()?.person?.dateDeath ?? this.person().dateDeath ?? null);

    protected isDeceased = computed(() => this.folderPerson()?.isDeceased ?? !!this.dateDeath());

    /**
     * Pobyt na kartě jen u dalších osob složky - primární osoba je ve složce po celou její dobu,
     * takže by jen opakovala datumy složky. Bez pobytu (jednoosobová složka) není co zobrazit.
     */
    protected showFolderPersonStay = computed(() => !!this.folderPerson() && !this.isMainPerson());

    /**
     * Odpověď na otázku v popisku řádku ("Osoba je aktuálně na službě") - proto vždycky jen stav
     * k dnešku. Datum konce pobytu tu schválně není: ukončený pobyt je pro dnešek jen "není na
     * službě" a konec v budoucnu by na kartě tvrdil něco, co ještě neplatí. Celé datumy pobytů
     * jsou v dialogu historie, na který řádek odkazuje.
     */
    protected stayText = computed(() => {
        const stay = this.folderPerson();
        if (!stay) return '';

        // úmrtí přebíjí datumy pobytu - ten backend uzavřel sám dnem úmrtí
        if (this.isDeceased()) {
            return this.translate.instant('folder.persons.stay.deceased', {
                date: this.dateDeath() ? shortDate(this.dateDeath()!, 'cs-CZ') : '',
            }).trim();
        }

        // ukončený i teprve začínající pobyt - datum nezajímá, na kartě má být jen to, že osoba
        // na službě dnes není
        if (!this.isOnService()) {
            return this.translate.instant('folder.persons.stay.notOnService');
        }

        return this.translate.instant('folder.persons.stay.from', {
            from: shortDate(stay.dateFrom!, 'cs-CZ'),
        });
    });

    protected baseFields1st = [
        'birthdate',
    ];

    protected baseFields2nd = [
        'address',
    ];

    protected baseFields3rd = [
        'workplace',
        'groups',
    ];

    protected healthCareFields1st = [
        'insuranceNumber',
    ];

    protected healthCareFields2nd = [
        'contact',

    ];

    protected healthCareFields3rd = [
        'workgroup',
    ];

    protected socialGeneralFields1st = [
        'mobile',
        'phone',
        'email',
        'communikePassword',
    ];

    protected socialGeneralFields2nd = [
        'targetGroup',
        'keyWorker',
        'primaryContact',
        'dependence',
        'carePayment',
        'personRestriction',
        'serviceForm',
    ];

    protected socialNursingHomeFields = [
        'insuranceNumber',
    ];

    /** Jen pro neprimární osoby - u primární je pobyt daný samotnou složkou, viz showFolderPersonStay(). */
    protected folderPersonFields = [
        'folderPersonStay',
    ];

    protected socialCareFields = [
        'familyWithTriplets',
        'resistanceFighter',
        'rehabilitationPerson',
        'personFromLaborCamp',
        'spouseOfResistanceFighterOver70',
        'spouseOfRehabilitationPersonOver70',
        'spouseOfPersonFromLaborCampOver70',
        'livingInSecialPurposeApartment',
    ];

    protected socialStatisticsFields = [
        'residenceBeforeStartOfServiceProvision',
        'overindebtedClient',
        'clientHasPermanentResidenceInService',
        'clientHadResidenceAbroad',
        'permanentlyBedridden',
        'clientIsMobileWithHelp',
        'healthCareIsProvided',
    ];

    protected groupedFields = computed(() => {
        return {
            'basicInfo': [
                ...this.baseFields1st,
                ...this.isSocial() ? this.socialGeneralFields1st : [],
                ...this.isHealthCare() ? this.healthCareFields1st : [],
                ...this.isNursingHome() ? this.socialNursingHomeFields : [],
                ...this.baseFields2nd,
                ...this.isHealthCare() ? this.healthCareFields2nd : [],
                ...this.baseFields3rd,
                ...this.isHealthCare() ? this.healthCareFields3rd : [],
                ...this.isSocial() ? this.socialGeneralFields2nd : [],
                ...this.showFolderPersonStay() ? this.folderPersonFields : [],
            ],
            ...{
                'unpaid': this.isSocialCare() ? this.socialCareFields : [],
            },
            'statistics': this.isSocial() ? this.socialStatisticsFields : [],
        };
    });

    protected includedFields = computed(() => {
        return Object.values(this.groupedFields()).flat();
    });

    protected formVisibilities = computed(() => {
        // uložené nastavení karty zná jen vlastnosti, které existovaly v době uložení - nově
        // přidané (pobyt ve složce, ...) by jinak byly schované, dokud je uživatel nezapne
        const initialValues = { ...shownPropertiesDefaultValues, ...this.shownProperties() };

        const fields = this.includedFields();
        const group: { [key: string]: any } = {};

        fields.forEach(fieldName => {
            group[fieldName] = fb.control(initialValues[fieldName as keyof IShownProperties]);
        });

        return new AppFormGroup(group);
    });

    protected formSocial = computed(() => {
        const initialValues = this.person().socialData;

        const form = new AppFormGroup({
            // unpaid section
            familyWithTriplets: fb.control(initialValues?.familyWithTriplets || false),
            resistanceFighter: fb.control(initialValues?.resistanceFighter || false),
            rehabilitationPerson: fb.control(initialValues?.rehabilitationPerson || false),
            personFromLaborCamp: fb.control(initialValues?.personFromLaborCamp || false),
            spouseOfResistanceFighterOver70: fb.control(initialValues?.spouseOfResistanceFighterOver70 || false),
            spouseOfRehabilitationPersonOver70: fb.control(initialValues?.spouseOfRehabilitationPersonOver70 || false),
            spouseOfPersonFromLaborCampOver70: fb.control(initialValues?.spouseOfPersonFromLaborCampOver70 || false),
            livingInSecialPurposeApartment: fb.control(initialValues?.livingInSecialPurposeApartment || false),

            // statistics section
            overindebtedClient: fb.control(initialValues?.overindebtedClient || false),
            clientHasPermanentResidenceInService: fb.control(initialValues?.clientHasPermanentResidenceInService || false),
            clientHadResidenceAbroad: fb.control(initialValues?.clientHadResidenceAbroad || false),
            residenceBeforeStartOfServiceProvision: fb.control<AddressDetailObject | null>(initialValues?.residenceBeforeStartOfServiceProvision ? JSON.parse(initialValues.residenceBeforeStartOfServiceProvision) : null),
            permanentlyBedridden: fb.control(initialValues?.permanentlyBedridden || false),
            clientIsMobileWithHelp: fb.control(initialValues?.clientIsMobileWithHelp || false),
            healthCareIsProvided: fb.control(initialValues?.healthCareIsProvided || false),
        });

        if (!this.canEdit()) {
            form.disable();
        }

        return form;
    });

    protected readonly folderBillingForm = fb.group({
        useDownPayment: fb.control(false),
    });

    protected primaryContactPhones = computed(() => {
        const data = this.store.data.getPersonContacts();

        return data?.items?.map((contact: PersonContactGridItemObject) => ([contact.user.mobile, contact.user.phone]))
            ?.flat()
            ?.filter((phone: string) => phone) ?? [];
    });

    protected activeCarePayment = computed(() => {
        const data = this.store.data.getCarePayments();

        const trueFalse = data?.items?.length > 0;
        return this.translate.instant('common.trueFalseEnum.' + trueFalse);
    });

    protected serviceForm = computed(() => {
        const data = this.store.currentService()?.form;

        return (data || []).map((it: string) => {
            return this.translate.instant(`service.serviceFormEnum.${it}`);
        }).join(', ');
    });

    protected dependenceText = computed(() => {
        if (!this.store.data.getPersonDependences()?.items?.length) {
            return '-';
        }

        const data = this.store.data.getPersonDependences().items[0];

        if (data.state === PersonDependenceStateEnum.NOT_REQUESTED || !data.dependence) {
            return this.translate.instant('person.dependence.states.' + data.state);
        }

        const state = this.translate.instant('person.dependence.states.' + data.state);
        const degree = this.translate.instant('person.dependence.degrees.' + data.dependence.degree);
        const date = shortDate(data.from, 'cs-CZ');

        return this.translate.instant('person.dependence.text', { state, degree, date });
    });

    protected generalGroups = computed(() => {
        const activeGroups = this.folder().activeGroups?.filter((group) => group.group.type === ServiceGroupEnum.GENERAL)
            ?.map((group) => group.group.name);

        return activeGroups?.join(', ') || null;
    });

    protected targetGroups = computed(() => {
        const primaryLabel = this.translate.instant('folder.targetGroup.primary');
        const activeGroups = (this.folder().activeGroups ?? [])
            .filter((group) => group.group.type === ServiceGroupEnum.SOCIAL_TARGET)
            .sort((a, b) => Number(b.primary) - Number(a.primary))
            .map((group) => group.primary
                ? `${group.group.name} (${primaryLabel})`
                : group.group.name,
            );

        return activeGroups?.join(', ') || null;
    });

    protected isHealthCare = computed(() => this.store.currentService()?.type === ServiceTypeEnum.HEALTH_CARE);
    protected isSocial = computed(() => this.store.currentService()?.type === ServiceTypeEnum.SOCIAL);
    protected billingEnabled = computed(() => this.isSocial() && this.store.currentServiceBillingSettings().useCard);
    protected isSocialCare = computed(() => this.store.currentService()?.serviceType?.name === 'Pečovatelská služba');
    protected isNursingHome = computed(() => this.store.currentService()?.serviceType?.name === 'Domovy pro seniory' || this.store.currentService()?.serviceType?.name === 'Domovy se zvláštním režimem');
    protected canEdit = this.store.isAllowed('person.update');
    protected canEditFolder = this.store.isAllowed('folder.update');

    protected communikePassword: string | null = null;
    protected displayedCommunikePassword: string | null = null;
    protected showCommunikePassword = false;

    constructor() {
        super();

        // čerstvý pobyt z rodiče má přednost před lokálně překlopenou hvězdičkou
        effect(() => {
            this.folderPerson();
            untracked(() => this.mainOverride.set(null));
        });

        effect(() => {
            this.fetchLegalRestriction();
        });

        this.store.onSuccess(
            () => this.findActiveCarePayment(),
            'createCarePayment',
            'updateCarePayment',
            'deleteCarePayment',
        );

        effect(() => {
            this.findPrimaryContact();
            this.findActiveCarePayment();
        });

        effect(() => {
            this.folderBillingForm.patchValue(
                { useDownPayment: this.folder().useDownPayment },
                { emitEvent: false },
            );
        });

        effect(() => {
            if (this.isSocial() && this.person()) {
                this.fetchActiveDependenceDetails(this.person().id);
                this.fetchCommunikePassword(this.person().id);
            }
        });

        // formulář se přestavuje za běhu (pobyt osoby dorazí až po vykreslení karty), odběr proto
        // musí přeskočit na aktuální instanci - jinak se změny v nastavení karty přestanou ukládat
        effect((onCleanup) => {
            const sub = this.formVisibilities().valueChanges.subscribe((v) => {
                this.showPropertiesChanged.emit(v);
            });

            onCleanup(() => sub.unsubscribe());
        });
    }

    ngOnInit() {
        this.subs.push(
            this.formSocial().valueChanges.pipe(
                debounceTime(config.inputDebounceTime),
            ).subscribe(async (value) => {
                if (value) {
                    const residenceBeforeStartOfServiceProvision = await this.appendMpsvDistrict(
                        value.residenceBeforeStartOfServiceProvision,
                    );
                    const data: PersonSocialDataPutInput = {
                        ...value as PersonSocialDataPutInput,
                        residenceBeforeStartOfServiceProvision: residenceBeforeStartOfServiceProvision
                            ? JSON.stringify(residenceBeforeStartOfServiceProvision)
                            : null,
                    };
                    this.saveSocialValues(data);
                    contentGlobalSuccess.next('common.saved');
                }
            }),
        );
    }

    private async appendMpsvDistrict(address: AddressDetailObject | null | undefined): Promise<AddressDetailObject | null> {
        if (!address) return null;

        const existingDistrict = address.regionalStructure.find((item) => (
            item.type === 'regional.county' && item.isoCode
        ));
        if (existingDistrict) return address;

        const district = await this.ruianService.getSszRegion(address);
        if (!district) return address;

        return {
            ...address,
            regionalStructure: [
                ...address.regionalStructure.filter((item) => item.type !== 'regional.county'),
                {
                    name: district.nazev,
                    type: 'regional.county',
                    isoCode: district.kod,
                },
            ],
        };
    }

    onFileUpload(file: FileObject) {
        if (this.person()) {
            this.store.api.updatePerson({
                personId: this.person().id,
                image: file.name,
            });
        }
    }

    /** Osob s příznakem primární může být ve složce víc, server hlídá, že aspoň jedna zůstane. */
    protected async toggleMainPerson(): Promise<void> {
        const folderPerson = this.folderPerson();
        if (!folderPerson || !this.canToggleMain()) return;

        const isMain = !this.isMainPerson();

        await this.store.api.setMainFolderPerson({ id: folderPerson.id, isMain });
        // hvězdička se překlopí hned, rodič dotáhne zbytek
        this.mainOverride.set(isMain);
        this.folderPersonChanged.emit();
        contentGlobalSuccess.next(`folder.persons.messages.${isMain ? 'mainSet' : 'mainUnset'}`);
    }

    /** Volá se z menu akcí nad pobytem i z dialogu historie pobytů - čerstvý stav dotáhne rodič. */
    protected onFolderPersonChanged(): void {
        this.mainOverride.set(null);
        this.folderPersonChanged.emit();
    }

    /** Všechny intervaly pobytu této osoby ve složce, včetně už ukončených. */
    protected openStayHistoryDialog(): void {
        this.dialogService.open(
            FolderPersonsHistoryDialogComponent,
            {
                header: this.translate.instant('folder.persons.stayHistory'),
                styleClass: 'p-dialog-width-60',
                data: { folderId: this.folder().id, personId: this.person().id },
            },
        ).onClose.subscribe(() => this.onFolderPersonChanged());
    }

    /** U dalších osob složky se edituje osoba z karty (`edit/:personId`), ne vlastník složky. */
    protected async goToEdit() {
        const commands = this.isFolderOwner() ? ['../edit'] : ['../edit', this.person().id];

        await this.router.navigate(commands, { relativeTo: this.route });
    }

    protected openWorkplacesDialog() {
        this.dialogService.open(
            SelectWorkplaceDialogComponent,
            {
                header: this.translate.instant('workplace.label'),
                styleClass: 'p-dialog-width-30',
            },
        );
    }

    protected openGroupDialog(type: ServiceGroupEnum) {
        this.dialogService.open(
            SelectFolderGroupsDialogComponent,
            {
                header: this.translate.instant(`folder.${type === ServiceGroupEnum.SOCIAL_TARGET ? 'targetGroup' : 'groups'}.labelMulti`),
                styleClass: 'p-dialog-width-60',
                data: { type },
            },
        );
    }

    protected openWorkgroupDialog() {
        this.dialogService.open(
            SelectFolderWorkgroupDialogComponent, {
                header: this.translate.instant('folder.workgroups.labelMulti'),
                styleClass: 'p-dialog-width-50',
            });
    }

    protected openCarePaymentDialog() {
        this.dialogService.open(
            CarePaymentDialogComponent,
            {
                header: this.translate.instant('folder.carePayment.label'),
                styleClass: 'p-dialog-width-50',
                data: {},
            },
        );
    }

    protected async updateFolderBillingSettings(useDownPayment?: boolean): Promise<void> {
        if (useDownPayment === undefined) return;

        await this.store.api.patchFolder({
            id: this.folder().id,
            useDownPayment,
        });
        await this.folderStore.refresh();
        contentGlobalSuccess.next('common.updated');
    }

    protected openKeyWorkerDialog() {
        this.dialogService.open(
            KeyWorkerComponent,
            {
                header: this.translate.instant('person.basicInfo.keyWorker'),
                data: {
                    folderId: this.folder().id,
                    keyWorkerId: this.folder().keyWorkerId || null,
                    serviceId: this.folder().serviceId,
                },
            },
        );
    }

    protected openDependenceDialog() {
        const dialog = this.dialogService.open(
            PersonDependenceDialogComponent,
            {
                header: this.translate.instant('person.dependence.title'),
                styleClass: 'p-dialog-width-50',
                data: {
                    personId: this.person().id,
                },
            },
        );

        dialog?.onClose?.subscribe(() => {
            this.fetchActiveDependenceDetails(this.person().id);
        });
    }

    protected editAddress() {
        this.dialogService.open(
            SelectFolderAddressDialogComponent,
            {
                header: this.translate.instant('person.address.message.selectCurrent'),
                styleClass: 'p-dialog-width-50',
                data: {
                    address: this.person().currentAddress,
                },
            },
        );
    }

    protected toggleCommunikePassword() {
        this.showCommunikePassword = !this.showCommunikePassword;
        if (this.showCommunikePassword) {
            this.displayedCommunikePassword = this.communikePassword;
        } else {
            if (this.communikePassword) {
                this.displayedCommunikePassword = '*'.repeat(this.communikePassword!.length);
            }
        }
    }

    private fetchLegalRestriction(): void {
        const filter = FilterBuilder.where({
            personId: this.person().id,
            type: PersonContactTypeEnum.RESTRICTION,
        });
        filter.validByNow();
        this.store.api.getPersonRestrictions(filter.filter(), 'id');
    }

    private findPrimaryContact() {
        if (!this.store.folderId()) return;
        const filter = new FilterBuilder();
        filter.where('folderIdsPersonId', [this.store.folderId(), this.person().id]);
        filter.where('type', PersonContactTypeEnum.CONTACT);
        filter.where('active', 'true');
        filter.where('primary', 'true');
        filter.sort('order');

        this.store.api.getPersonContacts(filter.singlePage(), personContactGridItemFrag);
    }

    private findActiveCarePayment() {
        if (!this.store.folderId() || !this.store.contactsSharingIds()) return;
        const now = new Date();
        const filter = new FilterBuilder();
        filter.where('folderId', [this.store.folderId(), ...this.store.contactsSharingIds()!], 'IN');
        filter.where('activeFrom', now.toISOString(), 'LTE');
        filter.where('activeTo', now.toISOString(), 'GTE');

        this.store.api.getCarePayments(filter.singlePage(), carePaymentFrag);
    }

    private saveSocialValues(values: PersonSocialDataPutInput) {
        this.store.api.updatePerson({
            personId: this.person().id,
            socialData: values,
        });
    }

    private fetchActiveDependenceDetails(personId: number) {
        const now = new Date();
        const filter = new FilterBuilder();
        filter.where('personId', personId);
        filter.where('from', now.toISOString(), 'LTE');
        filter.sort('from', 'DESC');
        filter.limit(1);

        this.store.api.getPersonDependences(filter.singlePage(), personDependenceFrag);
    }

    private fetchCommunikePassword(personId: number) {
        this.store.api.getPersonCommunicationPassword({ id: personId }).then((it: CommunicationPasswordObject) => {
            this.communikePassword = it.communikePassword;
            if (this.communikePassword) {
                this.displayedCommunikePassword = '*'.repeat(this.communikePassword!.length);
            }
        });
    }

    protected readonly Object = Object;
    protected readonly isAllowed = isAllowed;
    protected readonly ServiceGroupEnum = ServiceGroupEnum;

}
